import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Mail, ArrowLeft, ArrowRight, Loader2, CheckCircle2, Zap } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-yellow p-4">
      <div className="brutal bg-white max-w-md w-full p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 bg-brand-orange border-2 border-black rounded-xl flex items-center justify-center shadow-brutal">
            <Zap className="w-5 h-5 text-white" strokeWidth={3} />
          </div>
          <div>
            <div className="font-display font-black text-xl leading-none">ReferPro</div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mt-1">Reset password</div>
          </div>
        </div>

        {sent ? (
          <div data-testid="forgot-success">
            <div className="w-14 h-14 bg-brand-green border-2 border-black rounded-2xl flex items-center justify-center shadow-brutal mb-4">
              <CheckCircle2 className="w-7 h-7" strokeWidth={2.4} />
            </div>
            <h2 className="font-display font-black text-3xl">Check your inbox.</h2>
            <p className="text-sm text-neutral-700 mt-2">
              If an account exists for <strong>{email}</strong>, we've emailed you a link to set a new password. The link expires in 15 minutes.
            </p>
            <div className="text-xs text-neutral-500 mt-4 border-t-2 border-black/10 pt-3">
              Didn't get anything? Check spam — or ask your admin to verify your account email is correct.
            </div>
            <Link
              to="/login"
              className="btn-brutal btn-primary w-full mt-6"
              data-testid="back-to-login"
            >
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-display font-black text-4xl tracking-tight">Forgot password?</h1>
            <p className="text-neutral-600 mt-2 text-sm">
              Enter the email tied to your ReferPro account and we'll send you a reset link.
            </p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2">Email</label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-brutal pl-10"
                    autoComplete="email"
                    required
                    autoFocus
                    data-testid="forgot-email"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={busy}
                className="btn-brutal btn-primary w-full py-3"
                data-testid="forgot-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {busy ? "Sending…" : "Send reset link"}
              </button>
            </form>

            <Link
              to="/login"
              className="block text-center text-xs font-bold underline text-neutral-700 hover:text-black mt-4"
              data-testid="forgot-back"
            >
              ← Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
